import { cn } from "@/lib/utils";
import { useEffect, useState, useMemo } from "react";
import { useI18N } from "common/hooks";
import { CaretDown, PlusCircle } from "phosphor-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import { StandardButton } from "@/components/common/StandardButton";
import ScrollContainer from "react-indiana-drag-scroll";

type ActionTitleFilterProps = {
  selectedNoteType: "Mapper" | "Live Alerts" | "Notes" | undefined;
  setSelectedNoteType: (
    type: "Mapper" | "Live Alerts" | "Notes" | undefined,
  ) => void;
  mapperNotesCount: number;
  showMapperView: boolean;
  onSortChange?: (sortOrder: number) => void;
  venueNotes?: any[];
  // Props for Add Alert button
  showAddButton?: boolean;
  onAddClick?: () => void;
  // Props for scrolling
  onScrollToSection?: (sectionName: string) => void;
  liveAlertsCount?: number;
  notesCount?: number;
};

const ActionTitleFilter = ({
  selectedNoteType,
  setSelectedNoteType,
  mapperNotesCount,
  showMapperView,
  onSortChange,
  venueNotes,
  showAddButton,
  onAddClick,
  onScrollToSection,
  liveAlertsCount: liveAlertsCountProp,
  notesCount: notesCountProp,
}: ActionTitleFilterProps) => {
  const i18n = useI18N();
  const [sortByText, setSortByText] = useState("Date added (Newest)");

  // Set default tab on mount
  useEffect(() => {
    if (!selectedNoteType && setSelectedNoteType) {
      if (showMapperView && mapperNotesCount > 0) {
        setSelectedNoteType("Mapper");
      } else {
        // Count Live Alerts
        const liveAlertsCount =
          venueNotes?.filter((note) => {
            if (!note || typeof note !== "object") return false;
            if (note.note_type !== "Venue Note") return false;

            const actionTitle =
              note.action_title ||
              (note.note && note.note.action_title) ||
              (note.details && note.details.action_title);

            return actionTitle === "Live Alerts";
          }).length || 0;

        // Count Notes
        const notesCount =
          venueNotes?.filter((note) => {
            if (!note || typeof note !== "object") return false;
            if (note.note_type !== "Venue Note") return false;

            const actionTitle =
              note.action_title ||
              (note.note && note.note.action_title) ||
              (note.details && note.details.action_title);

            return actionTitle === "Notes" || !actionTitle;
          }).length || 0;

        // Smart default selection:
        // - If no Live Alerts but has Notes: select Notes
        // - Otherwise (including when both exist or neither exist): select Live Alerts
        if (liveAlertsCount === 0 && notesCount > 0) {
          setSelectedNoteType("Notes");
        } else {
          setSelectedNoteType("Live Alerts");
        }
      }
    }
  }, [
    showMapperView,
    mapperNotesCount,
    selectedNoteType,
    setSelectedNoteType,
    venueNotes,
  ]);

  const sortOptions = useMemo(
    () => [
      {
        id: 1,
        label: "Date added (Newest)",
      },
      {
        id: 2,
        label: "Date added (Oldest)",
      },
      {
        id: 3,
        label: "Venue area",
      },
    ],
    [],
  );

  useEffect(() => {
    if (onSortChange) {
      onSortChange(
        sortOptions.find((option) => option.label === sortByText)?.id || 1,
      );
    }
  }, [sortByText, onSortChange, sortOptions]);

  return (
    <div className="flex flex-col gap-0 w-full">
      {/* First row: Category tabs and Add Alert button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div role="tablist" aria-orientation="horizontal" aria-label="Note type" className="flex-1 min-w-0">
        <ScrollContainer className="flex items-center gap-1 overflow-x-auto">
          {(() => {
            const tabs: Array<{
              type: "Mapper" | "Live Alerts" | "Notes";
              label: string;
              count: number;
            }> = [];

            // Add Mapper tab if showMapperView and has notes
            if (showMapperView && mapperNotesCount > 0) {
              tabs.push({
                type: "Mapper",
                label: "Mapper actions",
                count: mapperNotesCount,
              });
            }

            // Use passed counts or calculate from venueNotes
            const liveAlertsCount =
              liveAlertsCountProp !== undefined
                ? liveAlertsCountProp
                : venueNotes?.filter((note) => {
                    if (!note || typeof note !== "object") return false;
                    if (note.note_type !== "Venue Note") return false;

                    const actionTitle =
                      note.action_title ||
                      (note.note && note.note.action_title) ||
                      (note.details && note.details.action_title);

                    return actionTitle === "Live Alerts";
                  }).length || 0;

            const notesCount =
              notesCountProp !== undefined
                ? notesCountProp
                : venueNotes?.filter((note) => {
                    if (!note || typeof note !== "object") return false;
                    if (note.note_type !== "Venue Note") return false;

                    const actionTitle =
                      note.action_title ||
                      (note.note && note.note.action_title) ||
                      (note.details && note.details.action_title);

                    return actionTitle === "Notes" || !actionTitle;
                  }).length || 0;

            // Add Live Alerts tab
            tabs.push({
              type: "Live Alerts",
              label: "Live Alerts",
              count: liveAlertsCount,
            });

            // Add Notes tab
            tabs.push({ type: "Notes", label: "Notes", count: notesCount });

            return tabs.map((tab, idx) => (
              <button
                key={tab.type}
                type="button"
                role="tab"
                className={cn(
                  "flex-shrink-0 min-w-[44px] min-h-[40px] px-3 py-2 rounded-lg whitespace-nowrap transition-colors",
                  selectedNoteType === tab.type
                    ? "label-m-semibold bg-bg-brand-tertiary-default border border-border-token-brand-subtle text-fg-brand-subtle"
                    : "label-m-medium bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover text-fg-neutral-subtle",
                )}
                tabIndex={selectedNoteType === tab.type ? 0 : -1}
                onClick={() => {
                  setSelectedNoteType(tab.type);
                  if (onScrollToSection) {
                    onScrollToSection(tab.type);
                  }
                }}
                onKeyDown={(e) => {
                  let newIdx = idx;
                  if (e.key === "ArrowRight") {
                    e.preventDefault();
                    newIdx = (idx + 1) % tabs.length;
                  } else if (e.key === "ArrowLeft") {
                    e.preventDefault();
                    newIdx = (idx - 1 + tabs.length) % tabs.length;
                  } else if (e.key === "Home") {
                    e.preventDefault();
                    newIdx = 0;
                  } else if (e.key === "End") {
                    e.preventDefault();
                    newIdx = tabs.length - 1;
                  } else {
                    return;
                  }
                  setSelectedNoteType(tabs[newIdx].type);
                  if (onScrollToSection) {
                    onScrollToSection(tabs[newIdx].type);
                  }
                }}
                aria-selected={selectedNoteType === tab.type}
              >
                {tab.label} ({tab.count})
              </button>
            ));
          })()}
        </ScrollContainer>
        </div>

        {/* Add Alert button */}
        {showAddButton && (
          <StandardButton
            variant="venueNew"
            onClick={onAddClick}
            className="w-full sm:w-auto flex-shrink-0"
          >
            <PlusCircle size={20} className="text-fg-neutral-strong" />
            <span className="label-m-medium">{i18n.get("addAlert")}</span>
          </StandardButton>
        )}
      </div>

      {/* Second row: Reports count and Sort dropdown - both on the right */}
      <div className="py-3 pr-3 flex flex-col sm:flex-row justify-between sm:justify-end items-start sm:items-center gap-3 sm:gap-4 border-t border-border-token-brand-minimal mt-3">
        {/* Reports count */}
        <div className="flex items-center">
          <div className="label-m-medium text-fg-neutral-strong">
            {(() => {
              if (selectedNoteType === "Mapper") {
                return mapperNotesCount;
              }
              if (selectedNoteType === "Live Alerts") {
                return liveAlertsCountProp !== undefined
                  ? liveAlertsCountProp
                  : venueNotes?.filter((note) => {
                      if (!note || typeof note !== "object") return false;
                      if (note.note_type !== "Venue Note") return false;

                      const actionTitle =
                        note.action_title ||
                        (note.note && note.note.action_title) ||
                        (note.details && note.details.action_title);

                      return actionTitle === "Live Alerts";
                    }).length || 0;
              }
              if (selectedNoteType === "Notes") {
                return notesCountProp !== undefined
                  ? notesCountProp
                  : venueNotes?.filter((note) => {
                      if (!note || typeof note !== "object") return false;
                      if (note.note_type !== "Venue Note") return false;

                      const actionTitle =
                        note.action_title ||
                        (note.note && note.note.action_title) ||
                        (note.details && note.details.action_title);

                      return actionTitle === "Notes" || !actionTitle;
                    }).length || 0;
              }
              return 0;
            })()}
            {" "}
            {i18n.get("reports")}
          </div>
        </div>

        {/* Sort Dropdown */}
        <div className="flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger>
              <div className="flex flex-row items-center gap-2 cursor-pointer">
                <span className="border-b border-border-token-brand-minimal label-s-medium text-fg-neutral-strong whitespace-nowrap">
                  {sortByText}
                </span>
                <CaretDown size={20} className="text-fg-neutral-strong" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="label-m-medium text-fg-neutral-strong bg-bg-brand-secondary-default  border border-border-token-brand-minimal rounded-lg">
              <DropdownMenuLabel
                tabIndex={0}
                aria-label={i18n.get("sortBy")}
                className="focus-blue label-m-medium text-fg-neutral-strong"
              >
                {i18n.get("sortBy")}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup
                value={sortByText}
                onValueChange={(value) => {
                  setSortByText(value);
                  const selectedOption = sortOptions.find(
                    (option) => option.label === value,
                  );
                  if (selectedOption && onSortChange) {
                    onSortChange(selectedOption.id);
                  }
                }}
              >
                {sortOptions.map((option) => (
                  <DropdownMenuRadioItem key={option.id} value={option.label} className="hover:bg-bg-brand-secondary-hover label-m-medium text-fg-neutral-strong">
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default ActionTitleFilter;
